import { saveBillingDetails } from "api/billing";
import type { BillingDetailsDto, SaveBillingDetailsPayload } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, type ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import {
	COUNTRY_NAMES,
	detectCountryCode,
	getCountryCodeByName,
	getCountryName,
	INDIA_COUNTRY_NAME,
	isIndiaCountryName
} from "utilities";

import styles from "./BillingDetailsDialog.module.css";

/**
 * What the form actually holds. countryCode is excluded on purpose: the dialog edits the country
 * by name, and the ISO code is derived from that at submit time. Including it here would make it
 * a field the form has to render, validate and length-check, which it is not.
 */
type BillingForm = Omit<SaveBillingDetailsPayload, "countryCode">;

type BillingField = keyof BillingForm;

type BillingDetailsDialogProps = {
	/** Account name used to prefill the customer name for first-time entry. */
	defaultCustomerName: string;
	/** Saved details to prefill when editing; null on first entry. */
	initialDetails: BillingDetailsDto | null;
	onClose: () => void;
	/** Fires after a successful save so the caller can resume the checkout. */
	onSaved: (saved: BillingDetailsDto) => void;
};

type BillingFieldConfig = { autoComplete: string; labelKey?: string; name: BillingField };

const FIELDS_BEFORE_COUNTRY: BillingFieldConfig[] = [
	{ autoComplete: "name", name: "customerName" },
	{ autoComplete: "street-address", name: "billingAddress" },
	{ autoComplete: "address-level2", name: "city" }
];

const PIN_FIELD: BillingFieldConfig = { autoComplete: "postal-code", name: "pinCode" };

const REQUIRED_FIELDS: BillingField[] = ["customerName", "billingAddress", "city", "pinCode", "country"];

/**
 * State is only mandatory for India, where it resolves the GST state code that decides CGST + SGST
 * vs IGST. Several countries have no province level at all, so requiring it there would only force
 * a made-up value onto the invoice. Mirrors the API's `RequiresState`.
 */
const requiredFields = (isIndia: boolean): BillingField[] =>
	isIndia ? [...REQUIRED_FIELDS, "state"] : REQUIRED_FIELDS;

/**
 * Exact state/UT names from the backend's Indian GST state list — a saved state must match one of
 * these for the API to resolve its GST state code (intra- vs inter-state tax split).
 */
const INDIAN_STATES = [
	"Andaman and Nicobar Islands",
	"Andhra Pradesh",
	"Arunachal Pradesh",
	"Assam",
	"Bihar",
	"Chandigarh",
	"Chhattisgarh",
	"Dadra and Nagar Haveli and Daman and Diu",
	"Delhi",
	"Goa",
	"Gujarat",
	"Haryana",
	"Himachal Pradesh",
	"Jammu and Kashmir",
	"Jharkhand",
	"Karnataka",
	"Kerala",
	"Ladakh",
	"Lakshadweep",
	"Madhya Pradesh",
	"Maharashtra",
	"Manipur",
	"Meghalaya",
	"Mizoram",
	"Nagaland",
	"Odisha",
	"Puducherry",
	"Punjab",
	"Rajasthan",
	"Sikkim",
	"Tamil Nadu",
	"Telangana",
	"Tripura",
	"Uttar Pradesh",
	"Uttarakhand",
	"West Bengal"
];

/**
 * Country to preselect for a first-time address. The browser time zone is a hint only — the user can
 * change it freely, and it is the saved country that decides pricing and tax treatment.
 */
const detectedCountryName = (): string => {
	const code = detectCountryCode();

	return code ? getCountryName(code) : INDIA_COUNTRY_NAME;
};

/**
 * The country options, with any previously saved country kept selectable even when it is not a
 * recognised ISO name (older addresses were free text, so values like "USA" exist).
 */
const countryOptions = (savedCountry: string): string[] => {
	const saved = savedCountry.trim();

	return saved && !COUNTRY_NAMES.includes(saved) ? [saved, ...COUNTRY_NAMES] : COUNTRY_NAMES;
};

/** Billing address form shown before the first Pro purchase (checkout requires one on file). */
const BillingDetailsDialog = ({ defaultCustomerName, initialDetails, onClose, onSaved }: BillingDetailsDialogProps) => {
	const { t } = useTranslation("plansPage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const [form, setForm] = useState<BillingForm>({
		customerName: initialDetails?.customerName ?? defaultCustomerName,
		billingAddress: initialDetails?.billingAddress ?? "",
		city: initialDetails?.city ?? "",
		state: initialDetails?.state ?? "",
		pinCode: initialDetails?.pinCode ?? "",
		country: initialDetails?.country ?? detectedCountryName()
	});
	const [fieldErrors, setFieldErrors] = useState<Partial<Record<BillingField, string>>>({});
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [countries] = useState<string[]>(() => countryOptions(initialDetails?.country ?? ""));

	const isIndia = isIndiaCountryName(form.country);

	const closeOnEscape = useCallback(
		(event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				onClose();
			}
		},
		[onClose]
	);

	useEffect(() => {
		document.addEventListener("keydown", closeOnEscape);

		return () => document.removeEventListener("keydown", closeOnEscape);
	}, [closeOnEscape]);

	const onFieldChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		const { name, value } = event.target;
		setForm((current: BillingForm) => ({ ...current, [name]: value }));
		setFieldErrors((current: Partial<Record<BillingField, string>>) => ({ ...current, [name]: undefined }));
	}, []);

	// Changing country clears the state: an Indian state name is meaningless elsewhere, and a foreign
	// region name would wrongly resolve to no GST state code if the address moved back to India.
	const onCountrySelect = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		const { value } = event.target;
		setForm((current: BillingForm) => ({ ...current, country: value, state: "" }));
		setFieldErrors((current: Partial<Record<BillingField, string>>) => ({
			...current,
			country: undefined,
			state: undefined
		}));
	}, []);

	const onStateSelect = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		const { value } = event.target;
		setForm((current: BillingForm) => ({ ...current, state: value }));
		setFieldErrors((current: Partial<Record<BillingField, string>>) => ({ ...current, state: undefined }));
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			if (isSaving) {
				return;
			}

			const missing: Partial<Record<BillingField, string>> = {};
			requiredFields(isIndia).forEach((name: BillingField) => {
				if (!form[name].trim()) {
					missing[name] = t("billingDialog.required");
				}
			});
			// The API requires an ISO code and the form holds a name. A country saved as free text before
			// the dropdown existed ("USA") stays selectable but has no code, so ask for a list entry here
			// rather than let the server reject it with a field the user never sees.
			if (!missing.country && getCountryCodeByName(form.country) === null) {
				missing.country = t("billingDialog.countryUnknown");
			}
			if (Object.keys(missing).length > 0) {
				setFieldErrors(missing);

				return;
			}

			setErrorMessage("");
			setIsSaving(true);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				// The form holds the country by name because that is what the dropdown shows and what
				// older saved rows contain. The server decides price and tax treatment from the code,
				// so resolve it here rather than leaving the API to match free text. The check above has
				// already refused a name that resolves to nothing.
				const saved = await saveBillingDetails(
					{ ...form, countryCode: getCountryCodeByName(form.country) ?? undefined },
					token
				);
				onSaved(saved);
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("genericError"));
			} finally {
				setIsSaving(false);
			}
		},
		[form, getAccessToken, isIndia, isSaving, onSaved, t]
	);

	const renderTextField = (field: BillingFieldConfig): ReactElement => (
		<label key={field.name} className={styles.field} htmlFor={`billing-${field.name}`}>
			<span className={styles.fieldLabel}>{t(`billingDialog.${field.labelKey ?? field.name}`)}</span>
			<input
				autoComplete={field.autoComplete}
				className={fieldErrors[field.name] ? `${styles.input} ${styles.inputError}` : styles.input}
				disabled={isSaving}
				id={`billing-${field.name}`}
				maxLength={fieldLimits[field.name]}
				name={field.name}
				onChange={onFieldChange}
				type="text"
				value={form[field.name]}
			/>
			{fieldErrors[field.name] && <span className={styles.fieldError}>{fieldErrors[field.name]}</span>}
		</label>
	);

	return (
		<div className={styles.overlay}>
			<form
				ref={dialogRef}
				aria-labelledby="billing-details-title"
				aria-modal="true"
				className={styles.dialog}
				onSubmit={onSubmit}
				role="dialog">
				<header className={styles.header}>
					<h1 id="billing-details-title">{t("billingDialog.title")}</h1>
					<p>{t("billingDialog.description")}</p>
				</header>

				<div className={styles.fields}>
					{FIELDS_BEFORE_COUNTRY.map((field: BillingFieldConfig) => renderTextField(field))}

					<label className={styles.field} htmlFor="billing-country">
						<span className={styles.fieldLabel}>{t("billingDialog.country")}</span>
						<select
							autoComplete="country-name"
							className={fieldErrors.country ? `${styles.input} ${styles.inputError}` : styles.input}
							disabled={isSaving}
							id="billing-country"
							onChange={onCountrySelect}
							value={form.country}>
							<option value="" disabled>
								{t("billingDialog.selectCountry")}
							</option>
							{countries.map((name: string) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>
						{fieldErrors.country && <span className={styles.fieldError}>{fieldErrors.country}</span>}
					</label>

					{isIndia ? (
						<label className={styles.field} htmlFor="billing-state-select">
							<span className={styles.fieldLabel}>{t("billingDialog.state")}</span>
							<select
								className={fieldErrors.state ? `${styles.input} ${styles.inputError}` : styles.input}
								disabled={isSaving}
								id="billing-state-select"
								onChange={onStateSelect}
								value={form.state}>
								<option value="" disabled>
									{t("billingDialog.selectState")}
								</option>
								{INDIAN_STATES.map((name: string) => (
									<option key={name} value={name}>
										{name}
									</option>
								))}
							</select>
							{fieldErrors.state && <span className={styles.fieldError}>{fieldErrors.state}</span>}
						</label>
					) : (
						renderTextField({
							autoComplete: "address-level1",
							labelKey: "stateRegionOptional",
							name: "state"
						})
					)}

					{renderTextField(isIndia ? PIN_FIELD : { ...PIN_FIELD, labelKey: "postalCode" })}
				</div>

				{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

				<footer className={styles.footer}>
					<button className={styles.cancelButton} disabled={isSaving} onClick={onClose} type="button">
						{t("billingDialog.cancel")}
					</button>
					<button className={styles.saveButton} disabled={isSaving} type="submit">
						{isSaving ? t("billingDialog.saving") : t("billingDialog.save")}
					</button>
				</footer>
			</form>
		</div>
	);
};

export default BillingDetailsDialog;
